class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener(
        "click",
        this.close.bind(this)
      );
    }
    this.setHeaderCartIconAccessibility();
    this.initTermsCheckbox();
  }

  initTermsCheckbox() {
    // Track acceptance state — persists across footer re-renders
    this._termsAccepted = false;

    // Use event delegation on the cart-drawer element (never replaced)
    this.addEventListener('change', (e) => {
      if (!e.target.matches('[data-cart-acceptance-checkbox]')) return;
      this._termsAccepted = e.target.checked;
      this.toggleCheckoutButton();
    });

    this.addEventListener('click', (e) => {
      if (!e.target.closest('.cart__ctas')) return;
      if (!this._termsAccepted && this.querySelector('[data-cart-acceptance-checkbox]')) this.toggleCheckoutButton();
    });

    this.addEventListener('submit', (e) => {
      if (!e.target.matches('#CartDrawer-Form')) return;
      if (!this._termsAccepted && this.querySelector('[data-cart-acceptance-checkbox]')) {
        e.preventDefault();
        this.toggleCheckoutButton();
      }
    });

    // Watch for footer re-renders and restore state every time
    const footer = this.querySelector('.drawer__footer');
    if (footer) {
      new MutationObserver(() => this.restoreTermsState())
        .observe(footer, { childList: true, subtree: true });
    }
  }

  restoreTermsState() {
    const checkbox = this.querySelector('[data-cart-acceptance-checkbox]');
    if (!checkbox) return;

    // Skip animation when restoring state after re-render
    if (this._termsAccepted) {
      const checkmark = this.querySelector('.cart__terms-checkmark');
      if (checkmark) {
        checkmark.classList.add('no-transition');
        requestAnimationFrame(() => {
          checkbox.checked = true;
          this.toggleCheckoutButton();
          requestAnimationFrame(() => checkmark.classList.remove('no-transition'));
        });
        return;
      }
    }

    checkbox.checked = this._termsAccepted;
    this.toggleCheckoutButton();
  }

  toggleCheckoutButton() {
    const btn = this.querySelector('#CartDrawer-Checkout');
    if (!btn) return;
    const hasTerms = !!this.querySelector('[data-cart-acceptance-checkbox]');
    const shouldDisable = hasTerms && !this._termsAccepted;
    btn.disabled = shouldDisable;
    btn.classList.toggle('cart__checkout-button--disabled', shouldDisable);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (!this.hasAttribute("disabled")) {
      document.body.classList.add("overflow-hidden");
    }
  }

  close() {
    this.classList.remove("active", "animate");
    removeTrapFocus(this.activeElement);
    // Always ensure scroll lock is removed
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const innerHtml = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );

      if (innerHtml) {
        if (sectionElement) {
          sectionElement.innerHTML = innerHtml;
        } else if (section.selector && section.selector === '.drawer__inner-recommendations-mobile') {
          // Create the recommendations container if it doesn't exist (when cart goes from empty to non-empty)
          const drawerBody = this.querySelector('.drawer__body-content');
          if (drawerBody) {
            const recommendationsDiv = document.createElement('div');
            recommendationsDiv.className = 'drawer__inner-recommendations-mobile';
            recommendationsDiv.innerHTML = innerHtml;
            // Insert before the footer
            const footer = this.querySelector('.drawer__footer');
            if (footer && footer.parentElement) {
              footer.parentElement.insertBefore(recommendationsDiv, footer);
            } else {
              drawerBody.appendChild(recommendationsDiv);
            }
          }
        } else if (section.selector && section.selector === '.drawer__inner-gift-wrapping') {
          // Create the gift wrapping container if it doesn't exist (when cart goes from empty to non-empty)
          const footer = this.querySelector('.drawer__footer');
          if (footer) {
            let giftWrappingDiv = footer.querySelector('.drawer__inner-gift-wrapping');
            if (!giftWrappingDiv) {
              giftWrappingDiv = document.createElement('div');
              giftWrappingDiv.className = 'drawer__inner-gift-wrapping';
              // Insert before payment icons
              const paymentIcons = footer.querySelector('.cart__payment-options');
              if (paymentIcons) {
                footer.insertBefore(giftWrappingDiv, paymentIcons);
              } else {
                footer.appendChild(giftWrappingDiv);
              }
            }
            giftWrappingDiv.innerHTML = innerHtml;
          }
        }
      }
    });

    // Initialize form handlers for recommendations after content is rendered
    this.initRecommendationForms();

    // Only open drawer if it's not disabled and not already open (prevents layout shifts)
    if (!this.hasAttribute("disabled") && !this.classList.contains('active')) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  initRecommendationForms() {
    // Handle forms in cart drawer recommendations to prevent page reload
    const recommendationForms = this.querySelectorAll('.drawer__inner-recommendations-mobile form[data-type="add-to-cart-form"]');
    recommendationForms.forEach((form) => {
      // Skip if already has listener
      if (form.dataset.listenerAdded === 'true') return;
      
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

        // Show loading state
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
          const spinner = submitButton.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(form);
        const cartItems = document.querySelector('cart-drawer-items');
        if (cartItems) {
          formData.append('sections', cartItems.getSectionsToRender().map((section) => section.section));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((state) => {
            if (state.status) {
              console.error('Error adding to cart:', state.description);
              return;
            }

            // Update cart drawer
            this.renderContents(state);

            // Publish cart update event
            if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-drawer-recommendations',
                productVariantId: formData.get('id'),
              });
            }
          })
          .catch((error) => {
            console.error('Error adding item to cart:', error);
          })
          .finally(() => {
            // Remove loading state
            if (submitButton) {
              submitButton.removeAttribute('aria-disabled');
              submitButton.classList.remove('loading');
              const spinner = submitButton.querySelector('.loading-overlay__spinner');
              if (spinner) spinner.classList.add('hidden');
            }
          });
      });

      form.dataset.listenerAdded = 'true';
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner > .drawer__header",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: "cart-drawer-items",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner-recommendations-mobile",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__footer",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner-gift-wrapping",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: "#cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner > .drawer__header",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: "cart-drawer-items",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner-recommendations-mobile",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__footer",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Handle AJAX form submissions in cart drawer recommendations
// class CartDrawerRecommendations extends HTMLElement {
//   constructor() {
//     super();
//     this.cartUpdateUnsubscriber = undefined;
//     this.removedProducts = []; // Products that were shown, added to cart, then removed
//     this.isRefreshing = false; // Prevent infinite refresh loops
//   }

//   connectedCallback() {
//     // Randomize products on first load - use setTimeout to ensure DOM is ready
//     if (!this.dataset.randomized) {
//       setTimeout(() => {
//         this.randomizeProducts();
//         this.dataset.randomized = 'true';
//       }, 0);
//     }
    
//     // Store initial product IDs to maintain sticky recommendations
//     if (!this.dataset.initialized) {
//       setTimeout(() => {
//         const productContainers = this.querySelectorAll('.cart-drawer--recommendations--product');
//         const productIds = Array.from(productContainers).map(container => container.dataset.productId);
//         this.dataset.initialProductIds = productIds.join(',');
//         this.dataset.initialized = 'true';
//       }, 0);
//     }

//     this.updateProductVisibility();
//     this.initForms();
    
//     // Subscribe to cart updates
//     if (typeof subscribe !== 'undefined' && window.PUB_SUB_EVENTS) {
//       this.cartUpdateUnsubscriber = subscribe(
//         PUB_SUB_EVENTS.cartUpdate,
//         (event) => {
//           if (event.source !== 'cart-drawer-recommendations') {
//             setTimeout(() => {
//               this.handleCartItemRemoval();
//             }, 100);
//           }
//         }
//       );
//     }
//   }

//   disconnectedCallback() {
//     if (this.cartUpdateUnsubscriber) {
//       this.cartUpdateUnsubscriber();
//     }
//   }

//   randomizeProducts() {
//     const container = this.querySelector('.cart-drawer--recommendations--products-container');
//     if (!container) {
//       // Retry if container not found yet
//       setTimeout(() => this.randomizeProducts(), 50);
//       return;
//     }
    
//     const products = Array.from(container.querySelectorAll('.cart-drawer--recommendations--product'));
//     if (products.length <= 1) return; // No need to shuffle if 1 or 0 products
    
//     // Fisher-Yates shuffle algorithm
//     for (let i = products.length - 1; i > 0; i--) {
//       const j = Math.floor(Math.random() * (i + 1));
//       [products[i], products[j]] = [products[j], products[i]];
//     }
    
//     // Re-append products in shuffled order
//     products.forEach(product => container.appendChild(product));
//   }

//   handleCartItemRemoval() {
//     // Just update visibility - if we're below limit, updateProductVisibility will handle refreshing
//     this.updateProductVisibility();
//   }

//   initForms() {
//     const forms = this.querySelectorAll('form[data-type="add-to-cart-form"]');
//     forms.forEach((form) => {
//       if (!form.dataset.listenerAdded) {
//         form.addEventListener('submit', (e) => {
//           e.preventDefault();
//           const cartDrawer = document.querySelector('cart-drawer');
//           if (!cartDrawer) {
//             form.submit();
//             return;
//           }

//           const submitButton = form.querySelector('button[type="submit"]');
//           if (submitButton) {
//             submitButton.setAttribute('aria-disabled', 'true');
//             submitButton.classList.add('loading');
//             const spinner = submitButton.querySelector('.loading-overlay__spinner');
//             if (spinner) spinner.classList.remove('hidden');
//           }

//           const config = fetchConfig('javascript');
//           config.headers['X-Requested-With'] = 'XMLHttpRequest';
//           delete config.headers['Content-Type'];

//           const formData = new FormData(form);
//           const cartItems = document.querySelector('cart-drawer-items');
//           // Include recommendations wrapper only when adding from recommendations
//           const sectionsToRender = cartItems.getSectionsToRender();
//           sectionsToRender.push({
//             id: "CartDrawer",
//             section: "cart-drawer",
//             selector: ".cart-drawer-recommendations-wrapper",
//           });
//           formData.append('sections', sectionsToRender.map((section) => section.section));
//           formData.append('sections_url', window.location.pathname);
//           config.body = formData;

//           // Get the product ID that's being added
//           const productContainer = form.closest('.cart-drawer--recommendations--product');
//           const addedProductId = productContainer?.dataset.productId;
          
//           fetch(`${routes.cart_add_url}`, config)
//             .then((response) => response.json())
//             .then((state) => {
//               if (state.status) {
//                 console.error('Error adding to cart:', state.description);
//                 return;
//               }
              
//               // Re-render recommendations to get replacement product
//               cartDrawer.renderContents(state);
              
//               // After re-render, update the recommendations
//               setTimeout(() => {
//                 const recommendationsElement = document.querySelector('cart-drawer-recommendations');
//                 if (recommendationsElement && addedProductId) {
//                   // Add the removed product to the back queue
//                   if (!recommendationsElement.removedProducts.includes(addedProductId)) {
//                     recommendationsElement.removedProducts.push(addedProductId);
//                   }
//                   // Randomize new products if section was re-rendered
//                   if (!recommendationsElement.dataset.randomized) {
//                     recommendationsElement.randomizeProducts();
//                     recommendationsElement.dataset.randomized = 'true';
//                   }
//                   recommendationsElement.updateProductVisibility();
//                   recommendationsElement.initForms();
//                 }
//               }, 100);
              
//               if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS) {
//                 publish(PUB_SUB_EVENTS.cartUpdate, {
//                   source: 'cart-drawer-recommendations',
//                   productVariantId: formData.get('id'),
//                 });
//               }
//             })
//             .catch((error) => {
//               console.error('Error adding item to cart:', error);
//             })
//             .finally(() => {
//               if (submitButton) {
//                 submitButton.removeAttribute('aria-disabled');
//                 submitButton.classList.remove('loading');
//                 const spinner = submitButton.querySelector('.loading-overlay__spinner');
//                 if (spinner) spinner.classList.add('hidden');
//               }
//             });
//         });
//         form.dataset.listenerAdded = 'true';
//       }
//     });
//   }

//   updateProductVisibility() {
//     // Get current cart product IDs
//     const cartItems = document.querySelectorAll('.cart-item[data-product-id]');
//     const cartProductIds = Array.from(cartItems).map(item => item.dataset.productId);
    
//     // Get the limit
//     const limit = parseInt(this.closest('.cart-drawer-recommendations-wrapper')?.dataset.limit || 3);
    
//     // Update visibility of recommendation products based on cart state
//     const productContainers = Array.from(this.querySelectorAll('.cart-drawer--recommendations--product'));
//     let visibleCount = 0;
    
//     productContainers.forEach((container) => {
//       const productId = container.dataset.productId;
//       if (cartProductIds.includes(productId)) {
//         container.style.display = 'none';
//       } else {
//         // Only show up to the limit
//         if (visibleCount < limit) {
//           container.style.display = '';
//           visibleCount++;
//         } else {
//           container.style.display = 'none';
//         }
//       }
//     });
    
//     // If we're below limit, we need to re-render to get more products
//     if (visibleCount < limit && !this.isRefreshing) {
//       this.refreshRecommendations();
//     }
//   }

//   refreshRecommendations() {
//     if (this.isRefreshing) return;
//     this.isRefreshing = true;
    
//     const cartItems = document.querySelector('cart-drawer-items');
//     if (!cartItems) {
//       this.isRefreshing = false;
//       return;
//     }
    
//     const sectionsToRender = cartItems.getSectionsToRender();
//     sectionsToRender.push({
//       id: "CartDrawer",
//       section: "cart-drawer",
//       selector: ".cart-drawer-recommendations-wrapper",
//     });
    
//     fetch(`${routes.cart_url}?sections=${sectionsToRender.map(s => s.section).join(',')}`)
//       .then(response => response.json())
//       .then(state => {
//         const wrapper = document.querySelector('.cart-drawer-recommendations-wrapper');
//         if (wrapper && state['cart-drawer']) {
//           const html = new DOMParser().parseFromString(state['cart-drawer'], 'text/html');
//           const newWrapper = html.querySelector('.cart-drawer-recommendations-wrapper');
//           if (newWrapper) {
//             wrapper.innerHTML = newWrapper.innerHTML;
//             // Re-initialize
//             const recommendationsElement = document.querySelector('cart-drawer-recommendations');
//             if (recommendationsElement) {
//               recommendationsElement.isRefreshing = false;
//               // Randomize new products after refresh
//               if (!recommendationsElement.dataset.randomized) {
//                 recommendationsElement.randomizeProducts();
//                 recommendationsElement.dataset.randomized = 'true';
//               }
//               recommendationsElement.updateProductVisibility();
//               recommendationsElement.initForms();
//             }
//           }
//         }
//         this.isRefreshing = false;
//       })
//       .catch(error => {
//         console.error('Error refreshing recommendations:', error);
//         this.isRefreshing = false;
//       });
//   }
// }

// customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);

class TieredProgressBar extends HTMLElement {
  constructor() {
    super();

    this.setProperties();

    if (this.tiers.length > 0 && this.currentTier != null) {
      this.render();
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source == "product-form") {
          setTimeout(() => {
            this.onCartUpdate();
          }, 300);
        } else {
          this.onCartUpdate();
        }
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  debug() {
    // console.log("*** DEBUG ***");
    // console.log("Settings: ", this.settings);
    // console.log("Current tier: ", this.currentTier);
    // console.log("Last tier: ", this.lastTier);
    // console.log("Is complete: ", this.isComplete);
    // console.log("Cart price: ", this.cartPrice);
    // console.log("Max price: ", this.maxPrice);
    // console.log("Line items", this.lineItems);
    // console.log("*** END DEBUG ***");
  }

  setProperties() {
    const cartDrawerItems = document.getElementById("CartDrawer-CartItems");
    this.cartPrice = parseFloat(
      (cartDrawerItems?.dataset.cartPrice
        ? parseFloat(cartDrawerItems.dataset.cartPrice)
        : 0
      )
        .toString()
        .replace(".", "")
        .replace(",", "")
    );
    this.settings = this.dataset.settings
      ? JSON.parse(this.dataset.settings)
      : {};
    this.tiers = this.settings.tiers;
    this.lastTier = this.tiers[this.tiers.length - 1];
    this.currentTier =
      this.tiers.find((tier) => this.cartPrice < parseFloat(tier.value)) ??
      this.lastTier;
    this.maxPrice = this.lastTier.value ? parseFloat(this.lastTier.value) : 0;
    this.isComplete =
      this.currentTier === this.lastTier && this.cartPrice >= this.maxPrice;

    this.lineItems = Array.from(
      document.querySelectorAll(".cart-items .cart-item")
    ).map((item) => {
      let properties = [];

      if (item.dataset.properties) {
        const propertiesString = item.dataset.properties.toString();
        let propArray = propertiesString
          .split(",")
          .filter((_) => _ != null)
          .filter((_) => _ != "");

        properties = propArray.map((property) => {
          const propSplit = property.split(":");

          return {
            name: propSplit[0],
            value: propSplit[1],
          };
        });
      }

      return {
        sku: item.dataset.sku,
        productId: item.dataset.productId,
        variantId: item.dataset.variantId,
        quantity: item.dataset.quantity ? parseInt(item.dataset.quantity) : 0,
        properties: properties,
      };
    });
  }

  render() {
    this.classList.remove("hide");

    try {
      const progressBarFill = this.querySelector(".tbp__progress-bar-fill");
      const progressBarText = this.querySelector(".tbp__progress-text");

      const progress = this.cartPrice / this.maxPrice;
      const progressPercentage = Math.min(100, Math.round(progress * 100));

      progressBarFill.style.width = `${progressPercentage}%`;

      if (this.isComplete) {
        progressBarText.innerText = this.currentTier.reachedText;
      } else {
        const remainingAmount = this.currentTier.value - this.cartPrice;
        const remainingAmountFormatted = this.formatMoney(
          remainingAmount,
          Shopify.currency.active
        );

        progressBarText.innerText = this.currentTier.text
          .replace("{{ remaining_amount }}", remainingAmountFormatted)
          .replace("{{remaining_amount}}", remainingAmountFormatted);
      }

      if (this.tiers.length > 1) {
        this.tiers.forEach((tier, i) => {
          const node = this.querySelector(`.tier-${i + 1}`);
          const isComplete = this.cartPrice >= tier.value;

          if (isComplete) {
            node.classList.add("is-complete");
          } else {
            node.classList.remove("is-complete");
          }
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  formatMoney(amount, currencyCode) {
    const { locales, maximumFractionDigits } =
      this.getNumberFormatSettings(currencyCode);

    return new Intl.NumberFormat(locales, {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits,
    }).format(amount);
  }

  getNumberFormatSettings(currencyCode) {
    switch (currencyCode) {
      case "EUR":
        return {
          locales: undefined,
          maximumFractionDigits: 2,
        };
      case "USD":
        return {
          locales: "en-US",
          maximumFractionDigits: 1,
        };
      case "DKK":
        return {
          locales: "da-DK",
          maximumFractionDigits: 0,
        };
      case "GBP":
        return {
          locales: "en-GB",
          maximumFractionDigits: 1,
        };
      case "SEK":
        return {
          locales: "sv-SE",
          maximumFractionDigits: 0,
        };
      case "NOK":
        return {
          locales: "nb-NO",
          maximumFractionDigits: 0,
        };
      default:
        return {
          locales: "en-US",
          maximumFractionDigits: 1,
        };
    }
  }

  onCartUpdate() {
    this.setProperties();
    this.render();

    this.tiers.forEach((tier, i) => {
      const isComplete = this.cartPrice >= tier.value;

      if (isComplete && tier.type === "free_product") {
        const lineItemInCart = this.lineItems.find(
          (item) =>
            item.variantId === tier.productVariantId &&
            item.properties.find(
              (x) => x.name === "_source" && x.value === "Cart Slider"
            )
        );

        if (!lineItemInCart) {
          this.addLineItem(Number(tier.productVariantId));
        }
      } else if (!isComplete && tier.type === "free_product") {
        const lineItemInCart = this.lineItems.find(
          (item) =>
            item.variantId === tier.productVariantId &&
            item.properties.find(
              (x) => x.name === "_source" && x.value === "Cart Slider"
            )
        );

        if (lineItemInCart) {
          const index = this.lineItems.findIndex(
            (item) => item.id === lineItemInCart.id
          );
          this.removeLineItem(index + 1);
        }
      }
    });
  }

  addLineItem(variantId) {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);
    formData.append("properties[_source]", "Cart Slider");
    formData.append("properties[_attribution]", "Free Gift with Purchase");

    const cartDrawer = document.querySelector("cart-drawer");
    const cartItems = document.querySelector("cart-drawer-items");
    formData.append(
      "sections",
      cartItems.getSectionsToRender().map((section) => section.section)
    );
    formData.append("sections_url", window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((state) => {
        cartDrawer.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          const innerHtml = cartDrawer.getSectionInnerHTML(
            state.sections[section.section],
            section.selector
          );

          if (innerHtml) {
            elementToReplace.innerHTML = innerHtml;
          }
        });
      })
      .catch((e) => {
        // console.error(e);
      })
      .finally(() => {});
  }

  removeLineItem(index) {
    const cartItems = document.querySelector("cart-drawer-items");
    cartItems.updateQuantity(index, 0);
  }
}

customElements.define("tiered-progress-bar", TieredProgressBar);
